<script setup lang="ts">
import { computed } from 'vue';

const props = defineProps<{
  badge: TMissionOrBadge | null;
  badges?: TMissionOrBadge[];
}>();

const emit = defineEmits<{
  (e: 'open-mission', missionId: number): void;
  (e: 'select-badge', badge: TMissionOrBadge): void;
}>();

const isVisible = defineModel<boolean>('modelValue', {
  required: false,
  default: false,
});

const router = useRouter();

const isCompleted = computed(() => props.badge?.is_completed === true);

// Quando vier "BRASIL - Name Real" usamos só a 1ª parte; pra customs sem "-",
// mantém o texto inteiro.
const displayName = computed(() => {
  const raw = props.badge?.name ?? '';

  return raw.split(' - ')[0]?.trim() || raw;
});

// Remove whitespace interno (CRM ocasionalmente entrega "/foo?open= 123").
function sanitizeUrl(raw: string): string | null {
  const cleaned = raw.replace(/\s+/g, '');

  return cleaned || null;
}

// custom_data vem do CRM Smartico como any (object/string/JSON-string).
// Aceita object com qualquer das chaves comuns, ou string direta.
function extractCustomDataUrl(data: unknown): string | null {
  if (!data) return null;

  let parsed: unknown = data;

  if (typeof data === 'string') {
    const trimmed = data.trim();

    if (!trimmed) return null;

    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        parsed = JSON.parse(trimmed);
      } catch {
        return sanitizeUrl(trimmed);
      }
    } else {
      return sanitizeUrl(trimmed);
    }
  }

  if (parsed && typeof parsed === 'object') {
    const obj = parsed as Record<string, unknown>;

    for (const key of ['url', 'link', 'route', 'href']) {
      const value = obj[key];

      if (typeof value === 'string' && value.trim()) {
        return sanitizeUrl(value);
      }
    }
  }

  return null;
}

const ctaUrl = computed(() =>
  props.badge ? extractCustomDataUrl(props.badge.custom_data) : null,
);

// CRM entrega description como JSON-string com:
//   - "Text": descrição principal (renderizada como parágrafo)
//   - "Local"/"Revealed": meta da figurinha, renderizada na barra abaixo dos chips
//   - demais campos: stats (GOL/DEF/...) no grid de chips label/valor
type ParsedDescription = {
  text: string | null;
  stats: { label: string; value: string }[];
  meta: { label: string; value: string }[];
};

const STAT_LABEL_OVERRIDES: Record<string, string> = {
  Revealed: 'Revelado',
};

const META_KEYS = new Set(['local', 'revealed']);

function parseDescription(raw: string | undefined): ParsedDescription | null {
  if (!raw) return null;

  const trimmed = raw.trim();

  if (!trimmed.startsWith('{')) return null;

  let parsed: unknown;

  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return null;
  }

  if (!parsed || typeof parsed !== 'object') return null;

  const obj = parsed as Record<string, unknown>;
  const result: ParsedDescription = { text: null, stats: [], meta: [] };

  for (const [key, value] of Object.entries(obj)) {
    if (typeof value !== 'string' && typeof value !== 'number') continue;

    const valueStr = String(value).trim();

    if (!valueStr) continue;

    const lowerKey = key.toLowerCase();

    if (lowerKey === 'text') {
      result.text = valueStr;
      continue;
    }

    const entry = {
      label: STAT_LABEL_OVERRIDES[key] ?? key,
      value: valueStr,
    };

    if (META_KEYS.has(lowerKey)) {
      result.meta.push(entry);
    } else {
      result.stats.push(entry);
    }
  }

  return result;
}

const parsedDescription = computed(() =>
  parseDescription(props.badge?.description),
);

// Quando a URL traz `?open=<id>`, pulamos a navegação e emitimos pro pai abrir
// o modal da missão diretamente. Pai resolve o id no store de missions.
function tryExtractOpenMissionId(url: string): number | null {
  try {
    const parsed = new URL(url, 'http://placeholder');
    const open = parsed.searchParams.get('open');

    if (!open) return null;

    const id = Number(open);

    return Number.isFinite(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

const currentIndex = computed(() => {
  if (!props.badge || !props.badges?.length) return -1;

  return props.badges.findIndex((b) => b.id === props.badge!.id);
});

const hasPrev = computed(() => currentIndex.value > 0);
const hasNext = computed(
  () =>
    currentIndex.value >= 0 &&
    !!props.badges &&
    currentIndex.value < props.badges.length - 1,
);

function goPrev() {
  if (!hasPrev.value || !props.badges) return;
  emit('select-badge', props.badges[currentIndex.value - 1]!);
}

function goNext() {
  if (!hasNext.value || !props.badges) return;
  emit('select-badge', props.badges[currentIndex.value + 1]!);
}

function onKeydown(e: KeyboardEvent) {
  if (!isVisible.value) return;
  if (e.key === 'ArrowLeft') {
    e.preventDefault();
    goPrev();
  } else if (e.key === 'ArrowRight') {
    e.preventDefault();
    goNext();
  } else if (e.key === 'Escape') {
    isVisible.value = false;
  }
}

watch(isVisible, (open) => {
  if (typeof window !== 'undefined') {
    if (open) {
      window.addEventListener('keydown', onKeydown);
    } else {
      window.removeEventListener('keydown', onKeydown);
    }
  }
  if (typeof document === 'undefined') return;
  document.body.style.overflow = open ? 'hidden' : '';
});

onUnmounted(() => {
  if (typeof document !== 'undefined') {
    document.body.style.overflow = '';
  }
  if (typeof window !== 'undefined') {
    window.removeEventListener('keydown', onKeydown);
  }
});

function handleViewMission() {
  const url = ctaUrl.value;

  // Sem URL configurada no custom_data: click é no-op (botão segue visível).
  if (!url) return;

  const missionId = tryExtractOpenMissionId(url);

  if (missionId !== null) {
    // Detail fica aberto por baixo; MissionReward empilha por cima.
    emit('open-mission', missionId);

    return;
  }

  if (/^https?:\/\//i.test(url)) {
    window.open(url, '_blank', 'noopener,noreferrer');
  } else {
    router.push(url);
  }

  isVisible.value = false;
}
</script>

<template>
  <Teleport to="body">
    <Transition
      appear
      name="std-fade"
    >
      <div
        v-if="isVisible"
        class="std-overlay"
        @click.self="isVisible = false"
      >
        <div
          class="std-backdrop"
          @click="isVisible = false"
        />
        <Transition
          appear
          name="std-slide-up"
        >
          <div
            v-if="isVisible && badge"
            class="std-sheet"
          >
            <header class="std-header">
              <p class="sticker-detail__title">
                {{ badge.name }}
              </p>
              <button
                aria-label="Fechar"
                class="std-close"
                type="button"
                @click="isVisible = false"
              >
                <NuxtIcon
                  class="font-bold text-[16px]"
                  name="close"
                />
              </button>
            </header>

            <div class="std-content">
              <div class="sticker-detail__nav-row">
                <button
                  v-if="badges?.length"
                  aria-label="Figurinha anterior"
                  class="sticker-detail__nav-btn"
                  :disabled="!hasPrev"
                  type="button"
                  @click="goPrev"
                >
                  ❮
                </button>
                <div class="sticker-detail__img-wrapper">
                  <img
                    :alt="displayName"
                    class="sticker-detail__img"
                    :class="isCompleted ? 'is-unlocked' : 'is-locked'"
                    decoding="async"
                    draggable="false"
                    :src="badge.image"
                    @dragstart.prevent
                  />
                </div>
                <button
                  v-if="badges?.length"
                  aria-label="Próxima figurinha"
                  class="sticker-detail__nav-btn"
                  :disabled="!hasNext"
                  type="button"
                  @click="goNext"
                >
                  ❯
                </button>
              </div>
              <template v-if="parsedDescription">
                <p
                  v-if="parsedDescription.text"
                  class="sticker-detail__description"
                >
                  {{ parsedDescription.text }}
                </p>
                <div
                  v-if="parsedDescription.stats.length"
                  class="sticker-detail__stats"
                >
                  <div
                    v-for="stat in parsedDescription.stats"
                    :key="stat.label"
                    class="sticker-detail__stat"
                  >
                    <span class="sticker-detail__stat-label">{{
                      stat.label
                    }}</span>
                    <span class="sticker-detail__stat-value">{{
                      stat.value
                    }}</span>
                  </div>
                </div>
                <div
                  v-if="parsedDescription.meta.length"
                  class="sticker-detail__meta"
                >
                  <div
                    v-for="item in parsedDescription.meta"
                    :key="item.label"
                    class="sticker-detail__meta-item"
                  >
                    <span class="sticker-detail__meta-label">{{
                      item.label
                    }}</span>
                    <span class="sticker-detail__meta-value">{{
                      item.value
                    }}</span>
                  </div>
                </div>
              </template>
              <button
                v-if="!isCompleted"
                class="sticker-detail__cta"
                type="button"
                @click="handleViewMission"
              >
                Ver missão
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.std-overlay {
  position: fixed;
  inset: 0;
  z-index: 100000;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.std-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(3, 12, 39, 0.77);
  backdrop-filter: blur(4px);
}

.std-sheet {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  max-height: 100dvh;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  background: theme('colors.bg-primary');
  border: 1.038px solid rgba(41, 41, 255, 0.55);
  border-radius: 18px 18px 0 0;
  display: flex;
  flex-direction: column;
}

@media (min-width: 768px) {
  .std-overlay {
    justify-content: center;
    align-items: center;
    padding: 24px;
  }

  .std-sheet {
    max-height: calc(100dvh - 48px);
    border-radius: 14px;
  }
}

.std-header {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  background: theme('colors.bg-primary');
}

.std-close {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.7);
  transition: color 0.15s ease;
  flex-shrink: 0;
  cursor: pointer;
}

.std-close:hover {
  color: #fff;
}

.std-close :deep(svg) {
  width: 1.5em;
  height: 1.5em;
}

.std-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 20px calc(20px + env(safe-area-inset-bottom, 0px));
}

.std-fade-enter-active,
.std-fade-leave-active {
  transition: opacity 0.25s ease;
}
.std-fade-enter-from,
.std-fade-leave-to {
  opacity: 0;
}

.std-slide-up-enter-active {
  animation: std-slide-up 0.32s cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
  will-change: transform, opacity;
}
.std-slide-up-leave-active {
  animation: std-slide-up 0.22s cubic-bezier(0.4, 0, 1, 1) reverse forwards;
  will-change: transform, opacity;
}
@keyframes std-slide-up {
  0% {
    transform: translateY(100%);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

.sticker-detail__title {
  flex: 1;
  font-size: 13px;
  line-height: 20px;
  font-weight: 700;
  color: #fff;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.sticker-detail__description {
  text-align: center;
  font-size: 12px;
  line-height: 18px;
  color: rgba(255, 255, 255, 0.8);
  padding: 0 8px;
}

.sticker-detail__stats {
  width: 100%;
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.sticker-detail__stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 6px 8px;
  border-radius: 6px;
  background: rgba(41, 41, 255, 0.18);
  border: 1px solid rgba(41, 41, 255, 0.35);
  min-width: 0;
  text-align: center;
}

.sticker-detail__stat-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
}

.sticker-detail__stat-value {
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  word-break: break-word;
  line-height: 16px;
}

.sticker-detail__meta {
  width: 100%;
  margin-top: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid rgba(41, 41, 255, 0.55);
}

.sticker-detail__meta-item {
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
}

.sticker-detail__meta-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
}

.sticker-detail__meta-value {
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  word-break: break-word;
}

.sticker-detail__nav-row {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.sticker-detail__nav-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(6, 6, 20, 0.9);
  border: 1px solid rgba(41, 41, 255, 0.35);
  color: #fff;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.5);
  transition:
    background 0.2s,
    transform 0.2s,
    border-color 0.2s,
    opacity 0.2s;
}

.sticker-detail__nav-btn:disabled {
  opacity: 0.2;
  cursor: not-allowed;
}

.sticker-detail__nav-btn:hover:not(:disabled) {
  background: #2929ff;
  border-color: #2929ff;
  transform: scale(1.08);
}

.sticker-detail__img-wrapper {
  width: 70%;
  max-width: 220px;
  aspect-ratio: 3 / 4;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sticker-detail__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 8px;
  transition: filter 0.3s ease;
}

.sticker-detail__img.is-unlocked {
  opacity: 1;
  animation: sticker-detail-aura-pulse 2.6s ease-in-out infinite;
}

@keyframes sticker-detail-aura-pulse {
  0%,
  100% {
    filter: drop-shadow(0 0 5px rgba(210, 225, 255, 0.9))
      drop-shadow(0 0 14px rgba(80, 120, 255, 0.85))
      drop-shadow(0 0 26px rgba(41, 41, 255, 0.5))
      drop-shadow(0 10px 18px rgba(0, 0, 0, 0.4));
  }
  50% {
    filter: drop-shadow(0 0 8px rgba(230, 240, 255, 1))
      drop-shadow(0 0 20px rgba(110, 150, 255, 1))
      drop-shadow(0 0 36px rgba(41, 41, 255, 0.7))
      drop-shadow(0 10px 18px rgba(0, 0, 0, 0.4));
  }
}

.sticker-detail__img.is-locked {
  opacity: 0.75;
  filter: grayscale(95%) drop-shadow(0 4px 8px rgba(0, 0, 0, 0.4));
}

.sticker-detail__cta {
  margin-top: 20px;
  width: 100%;
  height: 40px;
  border-radius: 8px;
  background: #2929ff;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.5px;
  border: 1px solid rgba(41, 41, 255, 0.6);
  cursor: pointer;
  transition:
    filter 0.2s ease,
    background 0.2s ease;
}

.sticker-detail__cta:hover {
  filter: brightness(1.1);
}
</style>
